import { createBrowserRouter } from "react-router";

import { RouteError } from "@/components/RouteError";
import { RouteLoading } from "@/components/RouteLoading";
import { HomeRedirect } from "@/components/HomeRedirect";
import { AppShell } from "@/components/layout/AppShell";
import { LegacyCryptoRedirect } from "@/pages/crypto/LegacyCryptoRedirect";
import { NotFoundPage } from "@/pages/NotFoundPage";

export const router = createBrowserRouter([
  {
    element: <AppShell />,
    hydrateFallbackElement: <RouteLoading />,
    children: [
      {
        errorElement: <RouteError />,
        children: [
          { path: "/", element: <HomeRedirect /> },
          {
            path: "/live",
            lazy: async () => ({
              Component: (await import("@/pages/live/LivePage")).LivePage,
            }),
          },
          {
            path: "/metrics",
            lazy: async () => ({
              Component: (await import("@/pages/metrics/MetricsPage"))
                .MetricsPage,
            }),
          },
          {
            path: "/metrics/otp",
            lazy: async () => ({
              Component: (await import("@/pages/metrics/otp/OtpInsightsPage"))
                .OtpInsightsPage,
            }),
          },
          {
            path: "/metrics/rails/:provider",
            lazy: async () => ({
              Component: (await import("@/pages/rails/RailLatencyPage"))
                .RailLatencyPage,
            }),
          },
          {
            path: "/support-load",
            lazy: async () => ({
              Component: (await import("@/pages/support-load/SupportLoadPage"))
                .SupportLoadPage,
            }),
          },
          {
            path: "/chargebacks",
            lazy: async () => ({
              Component: (await import("@/pages/chargebacks/ChargebacksPage"))
                .ChargebacksPage,
            }),
          },
          {
            path: "/rails/stale-transactions",
            lazy: async () => ({
              Component: (await import("@/pages/rails/StaleTransactionsPage"))
                .StaleTransactionsPage,
            }),
          },
          {
            path: "/users",
            lazy: async () => ({
              Component: (await import("@/pages/users/UsersPage")).UsersPage,
            }),
          },
          // Static before dynamic: "all" must never be parsed as a user id.
          {
            path: "/users/all",
            lazy: async () => ({
              Component: (await import("@/pages/users/all/AllUsersPage"))
                .AllUsersPage,
            }),
          },
          {
            path: "/users/:userId",
            lazy: async () => ({
              Component: (await import("@/pages/users/UserDetailPage"))
                .UserDetailPage,
            }),
          },
          {
            path: "/merchants",
            lazy: async () => ({
              Component: (await import("@/pages/merchants/MerchantsPage"))
                .MerchantsPage,
            }),
          },
          {
            path: "/merchants/view",
            lazy: async () => ({
              Component: (await import("@/pages/merchants/MerchantDetailPage"))
                .MerchantDetailPage,
            }),
          },
          {
            path: "/fraud",
            lazy: async () => ({
              Component: (await import("@/pages/fraud/FraudPage")).FraudPage,
            }),
          },
          {
            path: "/reviews",
            lazy: async () => ({
              Component: (await import("@/pages/reviews/ReviewsPage"))
                .ReviewsPage,
            }),
          },
          {
            path: "/kyc",
            lazy: async () => ({
              Component: (await import("@/pages/kyc/KycPage")).KycPage,
            }),
          },
          {
            path: "/stables-rfis",
            lazy: async () => ({
              Component: (await import("@/pages/stables-rfis/StablesRfisPage"))
                .StablesRfisPage,
            }),
          },
          {
            path: "/balances",
            lazy: async () => ({
              Component: (await import("@/pages/balances/BalancesPage"))
                .BalancesPage,
            }),
          },
          {
            path: "/crypto",
            element: <LegacyCryptoRedirect side="deposits" />,
          },
          {
            path: "/crypto/deposits",
            lazy: async () => {
              const { CryptoPage } = await import("@/pages/crypto/CryptoPage");
              return { Component: () => <CryptoPage side="deposits" /> };
            },
          },
          {
            path: "/crypto/withdrawals",
            lazy: async () => {
              const { CryptoPage } = await import("@/pages/crypto/CryptoPage");
              return { Component: () => <CryptoPage side="withdrawals" /> };
            },
          },
          // The crypto screens used to be two pages. Keep the old paths working —
          // bookmarks and pasted links carry the tab/item/status params that make
          // them worth sharing in the first place.
          {
            path: "/crypto-deposits",
            element: <LegacyCryptoRedirect side="deposits" />,
          },
          {
            path: "/crypto-withdrawals",
            element: <LegacyCryptoRedirect side="withdrawals" />,
          },
          {
            path: "/master-wallet",
            lazy: async () => ({
              Component: (
                await import("@/pages/master-wallet/MasterWalletPage")
              ).MasterWalletPage,
            }),
          },
          {
            path: "/admin-limits",
            lazy: async () => ({
              Component: (await import("@/pages/admin-limits/AdminLimitsPage"))
                .AdminLimitsPage,
            }),
          },
          {
            path: "/chatbot",
            lazy: async () => ({
              Component: (await import("@/pages/chatbot/ChatbotPage"))
                .ChatbotPage,
            }),
          },
          {
            path: "/chatbot/:conversationId",
            lazy: async () => ({
              Component: (await import("@/pages/chatbot/ChatbotPage"))
                .ChatbotPage,
            }),
          },
          {
            path: "/creator-program",
            lazy: async () => ({
              Component: (
                await import("@/pages/creator-program/CreatorProgramPage")
              ).CreatorProgramPage,
            }),
          },
          {
            path: "/announcements",
            lazy: async () => ({
              Component: (
                await import("@/pages/announcements/AnnouncementsPage")
              ).AnnouncementsPage,
            }),
          },
          {
            path: "/announcements/cards/:id",
            lazy: async () => ({
              Component: (
                await import("@/pages/announcements/HomeCardEditorPage")
              ).HomeCardEditorPage,
            }),
          },
          {
            path: "/feature-flags",
            lazy: async () => ({
              Component: (
                await import("@/pages/feature-flags/FeatureFlagsPage")
              ).FeatureFlagsPage,
            }),
          },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
